// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  KpiBoxes,
  PeriodPicker,
  ReturnsHeatMap,
  SectionContainer,
  ShowInAppAdvisor,
} from '@shared/ui-kit';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { Loader } from '../loader';
import { useReturns } from './use-returns';
import { ValueCreation } from './value-creation';

export const Returns = defineComponent({
  displayName: 'Returns',
  useSetup: useReturns,
  View: ({
    periodPicker,
    status,
    kpis,
    returns,
    accountsValueCreation,
    assetClassValueCreation,
    positionsValueCreation,
    tagsValueCreation,
    hasHoldings,
    currency,
    institutionId,
    onRefresh,
  }) => (
    <div className="mt-6 space-y-10">
      <Loader
        status={status}
        shouldDisplayEmptyState={!hasHoldings}
        institution={institutionId}
        onRefresh={onRefresh}
      >
        <div className="flex flex-col items-center justify-center gap-5 md:flex-row md:justify-start">
          <PeriodPicker
            onValueChange={periodPicker.onPeriodChange}
            availableOptions={['YTD', '1M', '3M', '6M', '1Y']}
            defaultValue={periodPicker.defaultHist}
          />
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {kpis.map((vals, idx) => (
            <KpiBoxes key={idx} {...vals} />
          ))}
        </div>

        <SectionContainer testId="returns-tab-content">
          <ReturnsHeatMap data={returns} />
        </SectionContainer>

        <SectionContainer testId="value-creation-accounts-tab-content">
          <ValueCreation
            name="Accounts"
            data={accountsValueCreation}
            data-testid="value-creation-accounts"
            currency={currency}
          />
        </SectionContainer>

        <SectionContainer testId="value-creation-asset-class-tab-content">
          <ValueCreation
            name="Asset Class"
            data={assetClassValueCreation}
            data-testid="value-creation-asset-class"
            currency={currency}
          />
        </SectionContainer>

        <ShowInAppAdvisor>
          <SectionContainer testId="value-creation-tags-tab-content">
            <ValueCreation
              name="Categories"
              data={tagsValueCreation}
              data-testid="value-creation-tags"
              currency={currency}
            />
          </SectionContainer>
        </ShowInAppAdvisor>

        <SectionContainer testId="value-creation-positions-tab-content">
          <ValueCreation
            name="Positions"
            data={positionsValueCreation}
            data-testid="value-creation-positions"
            currency={currency}
            uppercaseNameColumn
          />
        </SectionContainer>
      </Loader>
    </div>
  ),
});
